import type { Progress } from '../types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BadgeStats {
  totalLessons: number;   // across all pathways
  maxStreak: number;      // highest streakDays across all pathways
  subjectsTried: number;  // unique subjects with ≥1 lesson completed
  pathwaysCompleted: number;
  hasPerfectScore: boolean; // passed in at lesson-complete time
}

export interface BadgeDef {
  id: string;
  icon: string;
  nameKey: string;  // i18n key
  descKey: string;  // i18n key
  check: (stats: BadgeStats) => boolean;
}

// ─── Definitions (10 badges) ─────────────────────────────────────────────────

export const BADGES: BadgeDef[] = [
  {
    id: 'first-step',
    icon: '🌟',
    nameKey: 'badge.first_step.name',
    descKey:  'badge.first_step.desc',
    check: (s) => s.totalLessons >= 1,
  },
  {
    id: 'eager-reader',
    icon: '📚',
    nameKey: 'badge.eager_reader.name',
    descKey:  'badge.eager_reader.desc',
    check: (s) => s.totalLessons >= 5,
  },
  {
    id: 'knowledge-rocket',
    icon: '🚀',
    nameKey: 'badge.knowledge_rocket.name',
    descKey:  'badge.knowledge_rocket.desc',
    check: (s) => s.totalLessons >= 10,
  },
  {
    id: 'perfect-score',
    icon: '⭐',
    nameKey: 'badge.perfect_score.name',
    descKey:  'badge.perfect_score.desc',
    check: (s) => s.hasPerfectScore,
  },
  {
    id: 'on-fire',
    icon: '🔥',
    nameKey: 'badge.on_fire.name',
    descKey:  'badge.on_fire.desc',
    check: (s) => s.maxStreak >= 3,
  },
  {
    id: 'weekly-warrior',
    icon: '🏆',
    nameKey: 'badge.weekly_warrior.name',
    descKey:  'badge.weekly_warrior.desc',
    check: (s) => s.maxStreak >= 7,
  },
  {
    id: 'two-week-titan',
    icon: '💎',
    nameKey: 'badge.two_week_titan.name',
    descKey:  'badge.two_week_titan.desc',
    check: (s) => s.maxStreak >= 14,
  },
  {
    id: 'monthly-master',
    icon: '👑',
    nameKey: 'badge.monthly_master.name',
    descKey:  'badge.monthly_master.desc',
    check: (s) => s.maxStreak >= 30,
  },
  {
    id: 'explorer',
    icon: '🗺️',
    nameKey: 'badge.explorer.name',
    descKey:  'badge.explorer.desc',
    check: (s) => s.subjectsTried >= 2,
  },
  {
    id: 'path-complete',
    icon: '🎓',
    nameKey: 'badge.path_complete.name',
    descKey:  'badge.path_complete.desc',
    check: (s) => s.pathwaysCompleted >= 1,
  },
];

// ─── Helper: compute stats from all pathway progress ─────────────────────────

export function computeBadgeStats(
  progress: Record<string, Progress>,
  pathwayLessonCounts: Record<string, number>, // pathwayId → total lessons in pathway
  hasPerfectScore: boolean,
): BadgeStats {
  let totalLessons = 0;
  let maxStreak = 0;
  let pathwaysCompleted = 0;
  const subjects = new Set<string>();

  for (const [pathwayId, p] of Object.entries(progress)) {
    totalLessons += p.lessonsCompleted.length;
    if (p.streakDays > maxStreak) maxStreak = p.streakDays;
    const total = pathwayLessonCounts[pathwayId] ?? Infinity;
    if (p.lessonsCompleted.length >= total) pathwaysCompleted++;
  }

  // Subject tracking — derive from SAMPLE_LESSONS via caller if needed
  // For now, count progress entries as a proxy (each pathway = 1 subject)
  // Real subject tracking done via lesson.subject passed by caller
  return {
    totalLessons,
    maxStreak,
    subjectsTried: subjects.size,
    pathwaysCompleted,
    hasPerfectScore,
  };
}

// ─── Helper: which badge IDs are newly earned ────────────────────────────────

export function getNewlyEarned(
  stats: BadgeStats,
  alreadyEarned: string[],
): string[] {
  const earned = new Set(alreadyEarned);
  return BADGES
    .filter((b) => !earned.has(b.id) && b.check(stats))
    .map((b) => b.id);
}
